import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, type FieldErrors } from 'react-hook-form';
import { Building2, ChevronRight, Search } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ClientCreateRequest, type ClientStatus, type ClientSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  GstinLookupButton,
  GstinLookupNote,
  gstinHint,
  placeOfSupplyHint,
  useGstinAutoFill,
  useGstinLookup,
} from '@/components/GstinAssist';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const crmClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/clients',
  component: CrmClientsPage,
});

const FIELD_LABELS: Record<string, string> = {
  legalName: 'Legal name',
  displayName: 'Display name',
  gstin: 'GSTIN',
  pan: 'PAN',
  gstTreatment: 'GST treatment',
  leadChannelId: 'Lead channel',
  placeOfSupplyState: 'Place of supply (state)',
  paymentTermsDays: 'Payment terms (days)',
  billingAddress: 'Billing address',
};

type ClientSortKey = 'client' | 'gstin' | 'treatment' | 'channel' | 'terms' | 'status';
const EMPTY_CLIENTS: ClientSummary[] = [];

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase() || '?';
}

const STATUS_STYLE: Record<ClientStatus, { dot: string; pill: string }> = {
  active: {
    dot: 'bg-[hsl(var(--brand))]',
    pill: 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
  },
  archived: {
    dot: 'bg-[hsl(var(--muted-foreground))]',
    pill: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
  },
  merged: {
    dot: 'bg-[hsl(var(--secondary-foreground))]/50',
    pill: 'bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]',
  },
};

function StatusPill({ status }: { status: ClientStatus }): JSX.Element {
  const style = STATUS_STYLE[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${style.pill}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  );
}

type FormValues = {
  legalName: string;
  displayName?: string;
  gstin?: string;
  gstTreatment: 'registered' | 'unregistered' | 'composition' | 'exempt';
  pan?: string;
  billingAddress: string;
  placeOfSupplyState: string;
  paymentTermsDays: number;
  leadChannelId: string;
  primaryContact?: { name?: string; email?: string; phone?: string; title?: string };
};

function CrmClientsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<ClientStatus>('active');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['clients', status, search],
    queryFn: () => api.listClients({ status, search: search || undefined, page: 1, pageSize: 50 }),
  });
  const channelsQuery = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.listLeadChannels(),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(ClientCreateRequest),
    defaultValues: {
      legalName: '',
      gstTreatment: 'registered',
      billingAddress: '',
      placeOfSupplyState: '',
      paymentTermsDays: 30,
      leadChannelId: '',
    },
  });

  const channelById = React.useMemo(
    () => new Map((channelsQuery.data ?? []).map((c) => [c.id, c])),
    [channelsQuery.data],
  );

  const registered = form.watch('gstTreatment') === 'registered';
  const gstinValue = form.watch('gstin');
  const placeOfSupplyValue = form.watch('placeOfSupplyState');
  useGstinAutoFill(gstinValue, {
    getValue: (field) => form.getValues(field),
    setValue: (field, value) =>
      form.setValue(field, value, { shouldValidate: true, shouldDirty: true }),
  });
  const lookup = useGstinLookup((r) => {
    form.setValue('legalName', r.legalName, { shouldValidate: true, shouldDirty: true });
    if (r.tradeName) form.setValue('displayName', r.tradeName, { shouldDirty: true });
    form.setValue('pan', r.pan, { shouldValidate: true, shouldDirty: true });
    form.setValue('placeOfSupplyState', r.stateCode, { shouldValidate: true, shouldDirty: true });
    form.setValue('gstTreatment', r.suggestedGstTreatment, { shouldDirty: true });
    if (r.address) form.setValue('billingAddress', r.address, { shouldDirty: true });
  });
  const watchedContact = form.watch('primaryContact');
  const contactStarted = !!(
    watchedContact?.name ||
    watchedContact?.email ||
    watchedContact?.phone ||
    watchedContact?.title
  );

  const createMutation = useMutation({
    mutationFn: (body: FormValues) => {
      const contact = body.primaryContact;
      const contactTouched =
        !!contact && !!(contact.name || contact.email || contact.phone || contact.title);
      const primaryContact = contactTouched
        ? {
            name: contact.name ?? '',
            email: contact.email || undefined,
            phone: contact.phone || undefined,
            title: contact.title || undefined,
          }
        : undefined;
      return api.createClient({
        legalName: body.legalName,
        displayName: body.displayName || undefined,
        gstin: body.gstin || undefined,
        gstTreatment: body.gstTreatment,
        pan: body.pan || undefined,
        billingAddress: body.billingAddress ?? '',
        placeOfSupplyState: body.placeOfSupplyState,
        paymentTermsDays: body.paymentTermsDays,
        leadChannelId: body.leadChannelId,
        primaryContact,
        // Save means save: the near-duplicate review step was dropped by the
        // owner's decision, so the server is told to commit without suggesting.
        force: true,
      });
    },
    onSuccess: async (res) => {
      if (res.status !== 'created') return;
      setDialogOpen(false);
      form.reset();
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      await navigate({
        to: '/crm/clients/$clientId',
        params: { clientId: res.clientId },
        search: { saved: true },
      });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const submit = (values: FormValues): void => {
    setErrorMessage(null);
    createMutation.mutate(values);
  };

  // A long form hides its field errors above the fold, so a failed save also
  // says so next to the button, naming the fields to fix.
  const [blockedBy, setBlockedBy] = React.useState<string[]>([]);
  const onInvalid = (errors: FieldErrors<FormValues>): void => {
    const names = Object.keys(errors).flatMap((key) => {
      if (key === 'primaryContact') return ['Primary contact name'];
      return FIELD_LABELS[key] ? [FIELD_LABELS[key]] : [];
    });
    setBlockedBy(names);
  };
  React.useEffect(() => {
    if (blockedBy.length > 0 && form.formState.isValid) setBlockedBy([]);
  }, [blockedBy.length, form.formState.isValid]);

  const sortAccessors = React.useMemo<SortAccessors<ClientSummary, ClientSortKey>>(
    () => ({
      client: (c) => c.displayName,
      gstin: (c) => c.gstin,
      treatment: (c) => c.gstTreatment,
      channel: (c) => channelById.get(c.leadChannelId)?.name ?? null,
      terms: (c) => c.paymentTermsDays,
      status: (c) => c.status,
    }),
    [channelById],
  );
  const { rows: items, ...sorter } = useSortedRows(listQuery.data?.items ?? EMPTY_CLIENTS, sortAccessors);
  const total = listQuery.data?.total ?? 0;
  const channels = channelsQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Clients
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Archive replaces delete; merge preserves history.
          </p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setErrorMessage(null);
              form.reset();
              lookup.reset();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>New client</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New client</DialogTitle>
              <DialogDescription>GSTIN is checksum-validated.</DialogDescription>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(submit, onInvalid)} className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Legal name" required error={form.formState.errors.legalName?.message}>
                  <Input {...form.register('legalName')} />
                </Field>
                <Field label="Display name" error={form.formState.errors.displayName?.message}>
                  <Input {...form.register('displayName')} />
                </Field>
                <Field
                  label="GSTIN"
                  required={registered}
                  error={form.formState.errors.gstin?.message}
                  hint={gstinHint(gstinValue)}
                >
                  <div className="flex gap-2">
                    <Input className="font-mono uppercase" {...form.register('gstin')} />
                    <GstinLookupButton lookup={lookup} gstin={gstinValue} />
                  </div>
                </Field>
                <Field label="PAN" error={form.formState.errors.pan?.message}>
                  <Input {...form.register('pan')} />
                </Field>
                <Field label="GST treatment" error={form.formState.errors.gstTreatment?.message}>
                  <Select {...form.register('gstTreatment')}>
                    <option value="registered">Registered</option>
                    <option value="unregistered">Unregistered</option>
                    <option value="composition">Composition</option>
                    <option value="exempt">Exempt</option>
                  </Select>
                </Field>
                <Field
                  label="Lead channel"
                  required
                  error={form.formState.errors.leadChannelId?.message}
                >
                  <Select {...form.register('leadChannelId')}>
                    <option value="">Select…</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Place of supply (state)"
                  required
                  error={form.formState.errors.placeOfSupplyState?.message}
                  hint={placeOfSupplyHint(placeOfSupplyValue)}
                >
                  <Input {...form.register('placeOfSupplyState')} />
                </Field>
                <Field
                  label="Payment terms (days)"
                  error={form.formState.errors.paymentTermsDays?.message}
                >
                  <Input
                    type="number"
                    {...form.register('paymentTermsDays', { valueAsNumber: true })}
                  />
                </Field>
              </div>
              <GstinLookupNote lookup={lookup} />
              <Field label="Billing address" error={form.formState.errors.billingAddress?.message}>
                <Textarea rows={3} {...form.register('billingAddress')} />
              </Field>

              <div className="mt-2 border-t border-[hsl(var(--border))] pt-3">
                <p className="mb-2 text-xs font-medium text-[hsl(var(--muted-foreground))]">
                  Primary contact (optional)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Name"
                    required={contactStarted}
                    error={form.formState.errors.primaryContact?.name?.message}
                  >
                    <Input {...form.register('primaryContact.name')} />
                  </Field>
                  <Field label="Email" error={form.formState.errors.primaryContact?.email?.message}>
                    <Input type="email" {...form.register('primaryContact.email')} />
                  </Field>
                  <Field label="Phone" error={form.formState.errors.primaryContact?.phone?.message}>
                    <Input {...form.register('primaryContact.phone')} />
                  </Field>
                  <Field label="Title" error={form.formState.errors.primaryContact?.title?.message}>
                    <Input {...form.register('primaryContact.title')} />
                  </Field>
                </div>
              </div>

              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}

              {blockedBy.length > 0 && !errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Not saved yet</AlertTitle>
                  <AlertDescription>
                    Fix {blockedBy.length === 1 ? 'this field' : 'these fields'} and save again:{' '}
                    {blockedBy.join(', ')}.
                  </AlertDescription>
                </Alert>
              ) : null}

              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving...' : 'Save client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative w-full sm:max-w-sm sm:flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
          />
          <Input
            className="pl-9"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div className="flex self-start rounded-xl border border-[hsl(var(--border))] p-0.5 sm:self-auto">
          {(['active', 'archived', 'merged'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={
                'rounded-lg px-3 py-1.5 text-sm capitalize transition-colors ' +
                (status === s
                  ? 'bg-[hsl(var(--brand))] text-white'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]')
              }
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-end justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-1">
            <CardTitle className="capitalize">{status} clients</CardTitle>
            <CardDescription>
              {items.length === total
                ? `${total} ${total === 1 ? 'client' : 'clients'}`
                : `Showing ${items.length} of ${total}`}
            </CardDescription>
          </div>
          {listQuery.isFetching ? (
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Refreshing…</span>
          ) : null}
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <Building2 aria-hidden className="size-5" />
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {search
                  ? `No ${status} clients match “${search}”.`
                  : status === 'active'
                    ? 'No clients yet. Add the first one to start billing.'
                    : `No ${status} clients.`}
              </p>
              {search ? (
                <Button variant="outline" size="sm" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-y border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                    <SortableTh sortKey="client" controller={sorter} className="py-2.5 pl-6 pr-3">
                      Client
                    </SortableTh>
                    <SortableTh sortKey="gstin" controller={sorter} className="py-2.5 pr-3">
                      GSTIN
                    </SortableTh>
                    <SortableTh sortKey="treatment" controller={sorter} className="py-2.5 pr-3">
                      Treatment
                    </SortableTh>
                    <SortableTh sortKey="channel" controller={sorter} className="py-2.5 pr-3">
                      Channel
                    </SortableTh>
                    <SortableTh sortKey="terms" controller={sorter} align="right" className="py-2.5 pr-3">
                      Terms
                    </SortableTh>
                    <SortableTh sortKey="status" controller={sorter} className="py-2.5 pr-3">
                      Status
                    </SortableTh>
                    <th className="w-12 py-2.5 pr-4" aria-label="Open" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {items.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() =>
                        void navigate({ to: '/crm/clients/$clientId', params: { clientId: c.id } })
                      }
                      className="group cursor-pointer transition-colors hover:bg-[hsl(var(--accent))]/60"
                    >
                      <td className="py-3 pl-6 pr-3">
                        <div className="flex items-center gap-3">
                          <span
                            aria-hidden
                            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--accent))] font-display text-sm font-semibold text-[hsl(var(--accent-foreground))]"
                          >
                            {initials(c.displayName)}
                          </span>
                          <div className="min-w-0">
                            <Link
                              to="/crm/clients/$clientId"
                              params={{ clientId: c.id }}
                              onClick={(e) => e.stopPropagation()}
                              className="block truncate font-medium text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--brand))]"
                            >
                              {c.displayName}
                            </Link>
                            {c.legalName !== c.displayName ? (
                              <div className="truncate text-xs text-[hsl(var(--muted-foreground))]">
                                {c.legalName}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs tracking-wide">
                        {c.gstin ?? <span className="text-[hsl(var(--muted-foreground))]">—</span>}
                      </td>
                      <td className="py-3 pr-3">
                        <Badge variant="outline" className="capitalize">
                          {c.gstTreatment}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3 text-[hsl(var(--muted-foreground))]">
                        {channelById.get(c.leadChannelId)?.name ?? '—'}
                      </td>
                      <td className="py-3 pr-3 text-right tabular-nums">
                        {c.paymentTermsDays}
                        <span className="ml-1 text-xs text-[hsl(var(--muted-foreground))]">
                          days
                        </span>
                      </td>
                      <td className="py-3 pr-3">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <ChevronRight
                          aria-hidden
                          className="ml-auto size-4 text-[hsl(var(--muted-foreground))] opacity-0 transition-opacity group-hover:opacity-100"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
